import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { useArtistTracksQuery } from '~/modules/artist-roster/hooks/useArtistTracksQuery';
import { getNavLinkClassName } from '~/modules/artist-roster/pages/artist-details/utils/getNavLinkClassName';
import type { ArtistDetails } from '~/modules/artist-roster/types';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

interface TabLinksProps {
  artistData?: ArtistDetails;
}

/**
 * This component is wrapped in React.memo() to prevent unnecessary re-renders with
 * the ScrollCollapse logic. See https://github.com/wmgtech/wmg2-one-app/pull/1747
 */
export const TabLinks: React.FC<TabLinksProps> = memo(
  ({ artistData }: TabLinksProps) => {
    const { t } = useTranslation('roster');
    const { trackEvent } = useAnalytics('Artist Roster');

    const [isCampaignEnabled, isTrackPreReleasesEnabled] = useFeatureFlags([
      FEATURE_FLAGS.WmgCampaignBenchmarks,
      FEATURE_FLAGS.WmgTrackPreReleases,
    ]);

    const { data: tracksData } = useArtistTracksQuery({
      uaid: artistData?.uaid,
      pageNumber: 1,
      limit: 1,
      isReleased: isTrackPreReleasesEnabled ? true : undefined,
    });

    const { data: prereleasedData } = useArtistTracksQuery({
      uaid: artistData?.uaid,
      pageNumber: 1,
      limit: 1,
      isReleased: false,
    });

    const campaignLinkComponent = useMemo(
      () =>
        artistData?.uaid ? (
          <NavLink to="../campaigns" className={getNavLinkClassName}>
            {t('labels.campaigns')}
          </NavLink>
        ) : (
          <span className="cursor-default border-b pb-2xs border-transparent text-subtle">
            {t('labels.campaigns')}
          </span>
        ),
      [artistData?.uaid, t],
    );

    const handlePreReleaseTabsClick = () => {
      if (isTrackPreReleasesEnabled && artistData) {
        trackEvent('artistPreReleasesTabClick', {
          artist_id: artistData.partyId,
          artist_name: artistData.name,
        });
      }
    };

    return (
      <div
        className="flex gap-lg border-b text-body-sm font-semibold border-emphasis-subtle text-subtle"
        aria-label={t('labels.artistTabs')}
      >
        <NavLink to="../tracks" className={getNavLinkClassName}>
          {tracksData?.pagination
            ? t('labels.tracksCount', {
                count: tracksData?.pagination?.total || 0,
              })
            : t('labels.tracks')}
        </NavLink>
        {isTrackPreReleasesEnabled ? (
          <NavLink
            to="../pre-releases"
            className={getNavLinkClassName}
            onClick={handlePreReleaseTabsClick}
          >
            {prereleasedData?.pagination
              ? t('labels.preReleasesCount', {
                  count: prereleasedData?.pagination?.total || 0,
                })
              : t('labels.preReleases')}
          </NavLink>
        ) : null}
        {isCampaignEnabled ? campaignLinkComponent : null}
      </div>
    );
  },
);

TabLinks.displayName = 'TabLinks';
